import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NaturalLanguageFiller, type FillerFieldDef } from '@/components/NaturalLanguageFiller';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import { AccountDuplicateBanner } from '@/components/forms/DuplicateBanner';
import {
  COUNTRIES,
  CURRENCIES,
  LIFECYCLE_STAGES,
  REVENUE_BUCKETS,
  TIME_ZONES,
  canonicaliseUrl,
  taxIdTypesForCountry,
  validateEmail,
  validateLinkedinUrl,
  validatePhone,
  validateTaxId,
  validateUrl,
} from '@/lib/validation';

const INDUSTRIES = [
  'Technology', 'Financial Services', 'Healthcare', 'Manufacturing',
  'Retail', 'Energy', 'Media', 'Logistics', 'Real Estate', 'Education',
];
const SIZE_BUCKETS = ['SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'] as const;

const FILLER_SCHEMA: FillerFieldDef[] = [
  { name: 'name', label: 'Company name', type: 'string', required: true },
  { name: 'legalName', label: 'Legal name', type: 'string' },
  { name: 'industry', label: 'Industry', type: 'string' },
  { name: 'sizeBucket', label: 'Size (SMALL/MEDIUM/LARGE/ENTERPRISE)', type: 'string' },
  { name: 'website', label: 'Website', type: 'string' },
  { name: 'country', label: 'Country (ISO-2)', type: 'string' },
  { name: 'taxId', label: 'Tax ID', type: 'string' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateAccountModal({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Required block
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [country, setCountry] = useState('');
  const [currencyPreference, setCurrencyPreference] = useState('');
  const [taxIdType, setTaxIdType] = useState('');
  const [taxId, setTaxId] = useState('');

  // Contact block
  const [website, setWebsite] = useState('');
  const [phoneMain, setPhoneMain] = useState('');
  const [emailMain, setEmailMain] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');

  // Location block
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [timeZone, setTimeZone] = useState('');

  // Classification block
  const [industry, setIndustry] = useState('');
  const [sizeBucket, setSizeBucket] = useState('');
  const [lifecycleStage, setLifecycleStage] = useState('PROSPECT');
  const [employeeCount, setEmployeeCount] = useState('');
  const [annualRevenueBucket, setAnnualRevenueBucket] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [description, setDescription] = useState('');

  // Wire state
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Auto-suggest currency / tax_id type from country.
  useEffect(() => {
    if (!country) return;
    const defaults: Record<string, { currency: string; taxType: string }> = {
      CO: { currency: 'COP', taxType: 'NIT' },
      MX: { currency: 'MXN', taxType: 'RFC' },
      PR: { currency: 'USD', taxType: 'EIN' },
      US: { currency: 'USD', taxType: 'EIN' },
      AR: { currency: 'ARS', taxType: 'CUIT' },
      CL: { currency: 'CLP', taxType: 'RUT' },
      PE: { currency: 'PEN', taxType: 'RUC' },
      BR: { currency: 'BRL', taxType: 'CNPJ' },
      EC: { currency: 'USD', taxType: 'RUC' },
      ES: { currency: 'EUR', taxType: 'VAT' },
    };
    const d = defaults[country];
    if (d) {
      if (!currencyPreference) setCurrencyPreference(d.currency);
      if (!taxIdType) setTaxIdType(d.taxType);
    }
  }, [country, currencyPreference, taxIdType]);

  const taxIdTypes = useMemo(() => taxIdTypesForCountry(country), [country]);
  const taxResult = useMemo(
    () => validateTaxId(taxId, country, taxIdType || undefined),
    [taxId, country, taxIdType],
  );
  const websiteErr = useMemo(() => validateUrl(website), [website]);
  const emailErr = useMemo(() => validateEmail(emailMain), [emailMain]);
  const phoneErr = useMemo(() => validatePhone(phoneMain), [phoneMain]);
  const linkedinErr = useMemo(() => validateLinkedinUrl(linkedinUrl), [linkedinUrl]);

  if (!open) return null;

  function reset() {
    setName(''); setLegalName(''); setCountry(''); setCurrencyPreference('');
    setTaxIdType(''); setTaxId(''); setWebsite(''); setPhoneMain(''); setEmailMain('');
    setLinkedinUrl(''); setRegion(''); setCity(''); setPostalCode(''); setTimeZone('');
    setIndustry(''); setSizeBucket(''); setLifecycleStage('PROSPECT');
    setEmployeeCount(''); setAnnualRevenueBucket(''); setTagsRaw(''); setDescription('');
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (websiteErr || emailErr || phoneErr || linkedinErr) return;
    if (!taxResult.ok) return;
    setSubmitting(true);
    setError(null);
    try {
      const tags = tagsRaw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      // Forward-compatible: every new field is included; backend
      // accepts the ones it knows about and ignores the rest until
      // V160 lands. After V160, every field persists.
      await apiClient.post('/accounts', {
        name: name.trim(),
        legalName: legalName.trim() || undefined,
        country: country || undefined,
        region: region.trim() || undefined,
        city: city.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        currencyPreference: currencyPreference || undefined,
        taxId: taxId.trim() || undefined,
        taxIdType: taxIdType || undefined,
        timeZone: timeZone || undefined,
        phoneMain: phoneMain.trim() || undefined,
        emailMain: emailMain.trim() || undefined,
        linkedinUrl: linkedinUrl.trim() ? canonicaliseUrl(linkedinUrl) : undefined,
        website: website.trim() ? canonicaliseUrl(website) : undefined,
        industry: industry || undefined,
        sizeBucket: sizeBucket || undefined,
        lifecycleStage,
        employeeCountEstimate: employeeCount ? Number(employeeCount) : undefined,
        annualRevenueBucket: annualRevenueBucket || undefined,
        tags: tags.length > 0 ? tags : undefined,
        description: description.trim() || undefined,
      });
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('createAccount.error.generic');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !submitting && name.trim().length > 0 &&
    !websiteErr && !emailErr && !phoneErr && !linkedinErr && taxResult.ok;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('createAccount.title')}</h3>
          <NaturalLanguageFiller
            fieldSchema={FILLER_SCHEMA}
            onApply={(values) => {
              if (values.name) setName(values.name);
              if (values.legalName) setLegalName(values.legalName);
              if (values.industry) setIndustry(values.industry);
              if (values.sizeBucket) {
                const upper = values.sizeBucket.toUpperCase();
                if ((SIZE_BUCKETS as readonly string[]).includes(upper)) setSizeBucket(upper);
              }
              if (values.website) setWebsite(values.website);
              if (values.country) {
                const upper = values.country.toUpperCase();
                if (COUNTRIES.find((c) => c.code === upper)) setCountry(upper);
              }
              if (values.taxId) setTaxId(values.taxId);
            }}
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Section 1: Identity (always open) */}
          <FormSection title={t('createAccount.section.identity')} defaultOpen>
            <FormField label={t('createAccount.field.name')} required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('createAccount.placeholder.name')}
                required
                autoFocus
              />
            </FormField>
            <AccountDuplicateBanner
              name={name}
              taxId={taxId}
              onOpen={(id) => {
                onClose();
                navigate(`/accounts/${id}`);
              }}
            />
            <FormGrid>
              <FormField
                label={t('createAccount.field.legalName')}
                hint={t('createAccount.hint.legalName')}
              >
                <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} />
              </FormField>
              <FormField label={t('createAccount.field.lifecycleStage')}>
                <select
                  value={lifecycleStage}
                  onChange={(e) => setLifecycleStage(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {LIFECYCLE_STAGES.map((s) => (
                    <option key={s} value={s}>
                      {t(`reference.lifecycleStage.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
          </FormSection>

          {/* Section 2: Location + Tax (always open — critical for LATAM) */}
          <FormSection title={t('createAccount.section.locationTax')} defaultOpen>
            <FormGrid>
              <FormField label={t('createAccount.field.country')}>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {t(c.nameKey)} ({c.code})
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t('createAccount.field.currencyPreference')}>
                <select
                  value={currencyPreference}
                  onChange={(e) => setCurrencyPreference(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
            <FormGrid cols={3}>
              <FormField
                label={t('createAccount.field.taxIdType')}
                hint={taxIdTypes.length > 0 ? taxIdTypes.join(' / ') : t('createAccount.hint.selectCountryFirst')}
              >
                <select
                  value={taxIdType}
                  onChange={(e) => setTaxIdType(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  disabled={taxIdTypes.length === 0}
                >
                  <option value="">—</option>
                  {taxIdTypes.map((tt) => (
                    <option key={tt} value={tt}>
                      {tt}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label={t('createAccount.field.taxId')}
                error={!taxResult.ok ? `${t(taxResult.errorKey!)} ${taxResult.example ? `(${taxResult.example})` : ''}` : null}
              >
                <Input value={taxId} onChange={(e) => setTaxId(e.target.value)} />
              </FormField>
              <FormField label={t('createAccount.field.timeZone')}>
                <select
                  value={timeZone}
                  onChange={(e) => setTimeZone(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {TIME_ZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
          </FormSection>

          {/* Section 3: Contact (collapsed by default) */}
          <FormSection title={t('createAccount.section.contact')} defaultOpen={false}>
            <FormGrid>
              <FormField label={t('createAccount.field.phoneMain')} error={phoneErr ? t(phoneErr) : null}>
                <Input value={phoneMain} onChange={(e) => setPhoneMain(e.target.value)} placeholder="+57 …" />
              </FormField>
              <FormField label={t('createAccount.field.emailMain')} error={emailErr ? t(emailErr) : null}>
                <Input
                  type="email"
                  value={emailMain}
                  onChange={(e) => setEmailMain(e.target.value)}
                  placeholder="contacto@…"
                />
              </FormField>
            </FormGrid>
            <FormField label={t('createAccount.field.website')} error={websiteErr ? t(websiteErr) : null}>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="acme.com" />
            </FormField>
            <FormField label={t('createAccount.field.linkedinUrl')} error={linkedinErr ? t(linkedinErr) : null}>
              <Input value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder="linkedin.com/company/…" />
            </FormField>
          </FormSection>

          {/* Section 4: More — location detail + firmographics + classification */}
          <FormSection title={t('createAccount.section.more')} defaultOpen={false}>
            <FormGrid>
              <FormField label={t('createAccount.field.region')}>
                <Input value={region} onChange={(e) => setRegion(e.target.value)} />
              </FormField>
              <FormField label={t('createAccount.field.city')}>
                <Input value={city} onChange={(e) => setCity(e.target.value)} />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('createAccount.field.postalCode')}>
                <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
              </FormField>
              <FormField label={t('createAccount.field.industry')}>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>
                      {ind}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
            <FormGrid cols={3}>
              <FormField label={t('createAccount.field.sizeBucket')}>
                <select
                  value={sizeBucket}
                  onChange={(e) => setSizeBucket(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {SIZE_BUCKETS.map((s) => (
                    <option key={s} value={s}>
                      {t(`reference.sizeBucket.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t('createAccount.field.employeeCount')}>
                <Input
                  type="number"
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(e.target.value)}
                  min="0"
                />
              </FormField>
              <FormField label={t('createAccount.field.annualRevenueBucket')}>
                <select
                  value={annualRevenueBucket}
                  onChange={(e) => setAnnualRevenueBucket(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {REVENUE_BUCKETS.map((r) => (
                    <option key={r} value={r}>
                      {t(`reference.revenueBucket.${r}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
            <FormField label={t('createAccount.field.tags')} hint={t('createAccount.hint.tags')}>
              <Input value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} placeholder="strategic, partner-referred, …" />
            </FormField>
            <FormField label={t('createAccount.field.description')}>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </FormField>
          </FormSection>

          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {submitting ? t('createAccount.submit.creating') : t('createAccount.submit.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
